import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Flex,
  message,
  Popover,
  Radio,
  Space,
  Spin,
  Table,
  Button,
  Row,
  Col,
  Progress
} from "antd";
import "./component/index.scss";
import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import { formatDateText } from "@/utils/date";
import infoOutlined from "@/assets/images/icons/infoOutlined.png";
import { formatAmount } from '@/components/formatter';
import {
  CaretDownOutlined,
  OneToOneOutlined,
  SyncOutlined,
  CloseOutlined,
  RiseOutlined 
} from "@ant-design/icons";

const FinancialDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [allTableData, setAllTableData] = useState([]);
  const [moreLoading, setMoreLoading] = useState(false);
  const [currentWeekPage, setCurrentWeekPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showInfoBar, setShowInfoBar] = useState(true);

  const rowOptions = [
    { label: "Tenant Cash Received", value: "tenant" },
    { label: "Total", value: "total" },
  ];
  const [selectedItems, setSelectedItems] = useState(
    rowOptions.map((option) => option.value)
  );
  
  const [rpsData, setRpsData] = useState({
    grandTotal: 0,
    typeTotals: {
      rpsTenantStatement: 0,
      rpsCreditCheck: 0,
      rpsCheckOnly: 0
    },
    total: [] 
  });

  const fixedWeekOrder = [
    "Saturday",
    "Friday",
    "Thursday",
    "Wednesday",
    "Tuesday",
    "Monday",
    "Sunday",
  ];


  const buildRows = (weekData, weekIndex) => {
    if (!weekData || !Array.isArray(weekData)) return [];

    const dataMap = new Map();
    weekData.forEach((item) => {
      dataMap.set(item.day, {
        date: item.date,
        tenantAmount: item.tenantAmount,
        creditAmount: item.creditAmount,
        propertyAmount: item.propertyAmount,
        totalAmount: item.totalAmount,
      });
    });
    
    const rows = [];
    
    // 日期行
    const dateRow = {
      key: `week${weekIndex}_date`,
      entity: "",
      isDateRow: true,
      weekIndex,
    };
    
    fixedWeekOrder.forEach((day, idx) => {
      const data = dataMap.get(day);
      dateRow[`day_${idx}`] = data ? formatDateText(data.date) : "";
    });
    rows.push(dateRow);
    
    // Tenant Cash Received 行
    if (selectedItems.includes("tenant")) {
      const tenantRow = {
        key: `week${weekIndex}_tenant`,
        entity: "Tenant Cash Received",
        isDateRow: false,
        weekIndex,
      };
      fixedWeekOrder.forEach((day, idx) => {
        const data = dataMap.get(day);
        tenantRow[`day_${idx}`] = data ? commonFn.amountToDollarBracketFn(data.tenantAmount) : "-";
      });
      rows.push(tenantRow);
    }

    // Total 行
    if (selectedItems.includes("total")) {
      const totalRow = {
        key: `week${weekIndex}_total`,
        entity: "Total",
        isDateRow: false,
        isTotal: true,
        weekIndex,
      };
      fixedWeekOrder.forEach((day, idx) => {
        const data = dataMap.get(day);
        totalRow[`day_${idx}`] = data ? commonFn.amountToDollarBracketFn(data.totalAmount) : "-";
      });
      rows.push(totalRow);
    }
    
    return rows;
  };

  const processAllWeeksData = (data) => {
    const modules = {
      tenant: data.rpsTenantStatement,
      creditCheck: data.rpsCreditCheck,
      property: data.rpsCheckOnly,
      total: data.total,
    };
    
    let allRows = [];
    const maxWeeks = 4;
    
    for (let weekNum = 1; weekNum <= maxWeeks; weekNum++) {
      const weekKey = `week${weekNum}`;
      const totalWeekData = modules.total?.[weekKey];
      
      if (totalWeekData && Array.isArray(totalWeekData) && totalWeekData.length > 0) {
        const weekItems = [];
        totalWeekData.forEach((dayItem) => {
          const date = dayItem.date;
          const day = dayItem.day;
          const tenantAmount = modules.tenant?.[weekKey]?.find(item => item.date === date)?.amount || 0;
          const creditAmount = modules.creditCheck?.[weekKey]?.find(item => item.date === date)?.amount || 0;
          const propertyAmount = modules.property?.[weekKey]?.find(item => item.date === date)?.amount || 0;
          const totalAmount = modules.total?.[weekKey]?.find(item => item.date === date)?.amount || 0;
          
          weekItems.push({
            date,
            day,
            tenantAmount,
            creditAmount,
            propertyAmount,
            totalAmount,
          });
        });
        
        if (weekItems.length > 0) {
          const weekRows = buildRows(weekItems, weekNum);
          allRows.push(...weekRows);
        }
      }
    }
    
    setAllTableData(allRows);
  };

  const fetchRPSWeekReport = async () => {
    setLoading(true);
    setShowInfoBar(true);
    setCurrentWeekPage(1);
    setHasMore(true);       
    setMoreLoading(false);
    try {
      const res = await http.post(api.getShowTntTransWeekInit, {});
      if (res?.code !== "1") {
        message.error(res?.msg);
        return;
      }

      const data = res.data;
      if (!data) return;
      
      processAllWeeksData(data);
      
    } catch (error) {
      console.error("Failed to load RPS report:", error);
      message.error("Failed to load data, please try again later");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreWeeks = async () => {
    if (moreLoading || !hasMore) return;
    
    setMoreLoading(true);
    try {
      const res = await http.post(`${api.getShowTntTransWeekMore}?week=${currentWeekPage}`, {});
      if (res?.code !== "1") {
        message.error(res?.msg);
        return;
      }

      const data = res.data;
      if (!data) return;
      
      const modules = {
        tenant: data.rpsTenantStatement,
        creditCheck: data.rpsCreditCheck,
        property: data.rpsCheckOnly,
        total: data.total,
      };
      
      const newWeekRows = [];
      const weekKeys = Object.keys(modules.total || {});
      
      for (const weekKey of weekKeys) {
        const totalWeekData = modules.total?.[weekKey];
        if (totalWeekData && Array.isArray(totalWeekData) && totalWeekData.length > 0) {
          const weekItems = [];
          totalWeekData.forEach((dayItem) => {
            const date = dayItem.date;
            const day = dayItem.day;
            const tenantAmount = modules.tenant?.[weekKey]?.find(item => item.date === date)?.amount || 0;
            const creditAmount = modules.creditCheck?.[weekKey]?.find(item => item.date === date)?.amount || 0;
            const propertyAmount = modules.property?.[weekKey]?.find(item => item.date === date)?.amount || 0;
            const totalAmount = modules.total?.[weekKey]?.find(item => item.date === date)?.amount || 0;
            
            weekItems.push({
              date,
              day,
              tenantAmount,
              creditAmount,
              propertyAmount,
              totalAmount,
            });
          });
          
          if (weekItems.length > 0) {
            const weekNumber = parseInt(weekKey.replace('week', ''));
            const weekRows = buildRows(weekItems, weekNumber);
            newWeekRows.push(...weekRows);
          }
        }
      }
      
      if (newWeekRows.length > 0) {
        setAllTableData(prevData => [...prevData, ...newWeekRows]);
        setCurrentWeekPage(prev => prev + 1);
      } else {
        setHasMore(false);
        message.info(res?.msg || "No more data");
      }
      
    } catch (error) {
      console.error("Failed to load more weeks:", error);
      message.error(error?.response?.data?.msg || error?.msg || "Request failed");
    } finally {
      setMoreLoading(false);
    }
  };

  const handleChange = (checkedValues) => {
    setSelectedItems(checkedValues);
    fetchRPSWeekReport();
  };

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Checkbox.Group
        value={selectedItems}
        className="checkBox"
        onChange={handleChange}
      >
        <Space direction="vertical">
          {rowOptions.map((option) => (
            <Checkbox key={option.value} value={option.value}>
              {option.label}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
    </div>
  );

  useEffect(() => {
    fetchRPSWeekReport();
  }, []);

  // 统一的表格列配置
  const buildColumns = () => {
    const fixedColumn = {
      title: "RPS",
      dataIndex: "entity",
      width: 220,
      align: "center",
      fixed: 'left',
      render: (text, record) => {
        if (record.isDateRow) return null;
        return text;
      }
    };
    
    const dayColumns = fixedWeekOrder.map((day, idx) => ({
      title: day,
      dataIndex: `day_${idx}`,
      key: `day_${idx}`,
      align: "center",
      width: 130,
      render: (value, record) => {
        if (record.isDateRow) {
          return <span style={{ fontWeight: '600' }}>{value || ""}</span>;
        }
        return <div style={{ textAlign: 'right' }}>{value || ""}</div>;
      },
    }));
    
    return [fixedColumn, ...dayColumns];
  };

  const columns = buildColumns();
  const totalWidth = 60 + 220 + 7 * 130;

  // 自定义行渲染
  const getRowClassName = (record) => {
    if (record.isDateRow) return "date-row";
    if (record.isTotal) return "total-row";
    return "";
  };

  return (
    <div className="financial-dashboard">


        <Spin 
      spinning={loading} 
      tip="Loading..."
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '400px' 
      }}
    >
        {allTableData.length > 0 && (
          <div className="weeks-table-container">
            <Table
              columns={columns}
              dataSource={allTableData}
              pagination={false}
              tableLayout="fixed"
              size="small"
               scroll={{  x: "max-content",  y: "calc(100vh - 300px)",  }}
              rowClassName={getRowClassName}
              bordered
            />
            
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button 
                onClick={loadMoreWeeks} 
                loading={moreLoading}
                disabled={!hasMore}
                type="primary"
              >
                {moreLoading ? 'Loading...' : 'More'}
              </Button>
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default FinancialDashboard;